import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { css } from '@emotion/react';
import { matchPath, useLocation } from 'react-router-dom';
import { Modal, Typography } from '@uniquegood/realworld-studio-design';
import PromotionModal, { PaymentSuccessData } from '@/components/PromotionModal';
import { promotionModalConfig } from '@/components/PromotionModal/config';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import useModalState from '@/hooks/useModalState';
import { useRequest } from '@/hooks';
import {
  track,
  PageViewEventName,
  PromotionTriggerLocation,
  PLAN_TYPE_KOREAN,
} from '@/track';

interface OpenPromotionModalOptions {
  recommendedPlan?: Exclude<PlanType, 'Free'>;
  triggerLocation: PromotionTriggerLocation;
}

interface PromotionModalContextValue {
  openPromotionModal: (options: OpenPromotionModalOptions) => void;
  closePromotionModal: () => void;
}

const PromotionModalContext = createContext<PromotionModalContextValue | null>(null);

export function PromotionModalProvider({ children }: { children: React.ReactNode }) {
  const { plan } = usePlanLimits();
  const location = useLocation();
  const match = matchPath<{ appId: string }>(location.pathname, {
    path: '/apps/:appId',
    exact: false
  });
  const appId = match?.params?.appId;

  const { data: applicationInfo, mutate: mutateApplicationInfo } = useRequest<
    CommonResponseModel<ApplicationInfoResponseModel>
  >(appId ? `/apps/${appId}/info` : null);

  const [isPromotionOpen, setIsPromotionOpen] = useState(false);
  const [currentRecommendedPlan, setCurrentRecommendedPlan] = useState<
    Exclude<PlanType, 'Free'> | undefined
  >(undefined);
  const [currentTriggerLocation, setCurrentTriggerLocation] = useState<
    PromotionTriggerLocation | undefined
  >(undefined);
  const {
    modal: resultModal,
    openModal: openResultModal,
    closeModal: closeResultModal
  } = useModalState();

  // 다시 결제하기 버튼 클릭 시 사용할 함수를 저장
  const retryPaymentRef = useRef<(() => void) | null>(null);

  const openPromotionModal = useCallback(
    async (options: OpenPromotionModalOptions) => {
      setCurrentRecommendedPlan(options?.recommendedPlan);
      setCurrentTriggerLocation(options.triggerLocation);
      setIsPromotionOpen(true);

      // Mixpanel 이벤트 추적
      const applicationInfo = await mutateApplicationInfo();
      const channelInfo = applicationInfo?.data;
      

      track.onPageViewMixpanel(PageViewEventName.view_plan_promotion_modal, {
        channel_id: appId ?? '',
        channel_name: channelInfo?.name ?? '',
        use_purpose_type: channelInfo?.usePurposeType ?? '',
        plan_type: plan ?? '',
        trigger_location: options.triggerLocation
      });
    },
    [appId, applicationInfo, plan]
  );

  const closePromotionModal = useCallback(() => {
    setIsPromotionOpen(false);
  }, []);

  const handlePaymentSuccess = useCallback(
    (data: PaymentSuccessData) => {
      openResultModal({
        title: '프로 요금제 결제가 완료됐어요!',
        children: (
          <>
            <div>
              <Typography
                type="body"
                as="span"
                cssStyle={css`
                  color: #7800ff;
                `}
              >
                {data.appName}
              </Typography>
              <Typography type="body" as="span">
                에 프로 요금제가 적용됐어요.
              </Typography>
            </div>
            <Typography type="body">{`마음껏 ${data.userName}님의 아이디어를 펼쳐보세요!`}</Typography>
          </>
        ),
        primaryAction: {
          content: '확인',
          onAction: closeResultModal
        }
      });
    },
    [openResultModal, closeResultModal]
  );

  const handlePaymentFail = useCallback(
    (onRetry: () => void) => {
      retryPaymentRef.current = onRetry;

      openResultModal({
        title: '프로 요금제 결제에 실패했어요',
        children: (
          <Typography type="body">
            결제 실패가 반복되면 1:1 문의하기 버튼을 통해 문의글을 남겨 주세요.
          </Typography>
        ),
        primaryAction: {
          content: '다시 결제하기',
          onAction: () => {
            retryPaymentRef.current?.();
            closeResultModal();
          }
        },
        secondaryAction: {
          content: '1:1 문의하기',
          onAction: () => {
            if (typeof ChannelIO !== 'undefined' && ChannelIO) {
              ChannelIO('showMessenger');
            }
          }
        }
      });
    },
    [openResultModal, closeResultModal]
  );

  const contextValue: PromotionModalContextValue = {
    openPromotionModal,
    closePromotionModal
  };

  return (
    <PromotionModalContext.Provider value={contextValue}>
      {children}

      {/* PromotionModal - 앱 전체에서 단일 인스턴스만 렌더링 */}
      <Modal open={isPromotionOpen} onClose={closePromotionModal} {...promotionModalConfig}>
        <PromotionModal
          onClose={closePromotionModal}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentFail={handlePaymentFail}
          currentPlan={plan}
          recommendedPlan={currentRecommendedPlan}
          triggerLocation={currentTriggerLocation}
        />
      </Modal>

      {/* 결제 결과 모달 */}
      <Modal {...resultModal} />
    </PromotionModalContext.Provider>
  );
}

export function usePromotionModal(): PromotionModalContextValue {
  const context = useContext(PromotionModalContext);

  if (!context) {
    throw new Error('usePromotionModal must be used within a PromotionModalProvider');
  }

  return context;
}
